'use client';

import React, { useEffect, useRef } from 'react';
import { SHOPIFY_CONFIG } from '@/data/store';

interface AddToCartProps {
  text?: string;
  buyButtonId: string;
  id: string;
  background?: string;
  color?: string;
  hidePrice?: boolean;
  outline?: boolean;
  isLeft?: boolean;
  priceSize?: string;
  /** Font size for the struck-through "compare at" price. Should stay smaller than
   * priceSize so the sale price reads as the primary number. */
  compareAtSize?: string;
  showButton?: boolean;
  showOptions?: boolean;
  onAddToCart?: () => void;
  /** Matches the size/weight of the site's standard Button1 component (36px tall, 14px text,
   * no uppercase/letter-spacing) instead of the default 40px uppercase Buy Button style. Use
   * when this widget sits next to a Button1 (e.g. a card's CTA row) and must look consistent. */
  compact?: boolean;
  /** Called when the Shopify product lookup fails (e.g. a stale/unpublished buyButtonId
   * returns "product not found"). Callers can use this to fall back to a plain price display
   * instead of leaving a permanently blank widget. */
  onError?: () => void;
}

declare global {
  interface Window {
    ShopifyBuy?: any;
  }
}

const SHOPIFY_BUY_SDK_URL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';

/** Module-level singleton so N simultaneously-mounting AddToCart instances (e.g. a table/grid
 * with many rows entering the viewport at once) share a single script load instead of each
 * racing to inject its own duplicate <script> tag, which used to reassign window.ShopifyBuy
 * mid-init and cause some product components to silently fail to render. */
let shopifyBuySDKPromise: Promise<void> | null = null;

function loadShopifyBuySDK(): Promise<void> {
  if (typeof window !== 'undefined' && window.ShopifyBuy?.UI) {
    return Promise.resolve();
  }
  if (!shopifyBuySDKPromise) {
    shopifyBuySDKPromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = SHOPIFY_BUY_SDK_URL;
      script.onload = () => resolve();
      (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]).appendChild(script);
    });
  }
  return shopifyBuySDKPromise;
}

const AddToCart: React.FC<AddToCartProps> = ({
  buyButtonId,
  id,
  background,
  color,
  hidePrice = false,
  outline = false,
  isLeft = false,
  priceSize = '42px',
  compareAtSize,
  showButton = true,
  showOptions = true,
  onAddToCart,
  compact = false,
  onError,
}) => {
  const prevBuyButtonIdRef = useRef<string | undefined>(undefined);
  const componentRef = useRef<any>(null);


  useEffect(() => {
    const buyButtonChanged = prevBuyButtonIdRef.current?.toString() !== buyButtonId?.toString();

    if (buyButtonId && buyButtonChanged) {
      prevBuyButtonIdRef.current = buyButtonId;

      function ShopifyBuyInit() {
        if (!window.ShopifyBuy) {
          return;
        }

        const client = window.ShopifyBuy.buildClient({
          domain: SHOPIFY_CONFIG.domain,
          apiKey: SHOPIFY_CONFIG.apiKey,
          appId: SHOPIFY_CONFIG.appId,
        });

        window.ShopifyBuy.UI.onReady(client).then(function (ui: any) {
          const node = document.getElementById(`product-component-${id}`);
          if (!node) {
            return;
          }

          // Destroy any existing components first
          if ((ui as any).components && (ui as any).components.product) {
            (ui as any).components.product.forEach((component: any) => {
              try {
                if (component.node === node) {
                  component.destroy();
                }
              } catch (e) {
                console.warn('Failed to destroy component:', e);
              }
            });
          }

          // Clear existing content to force re-render
          node.innerHTML = '';

          const componentResult = ui.createComponent('product', {
            id: buyButtonId,
            node: node,
            moneyFormat: '%24%7B%7Bamount%7D%7D',
            options: {
              product: {
                width: '100%',
                contents: {
                  img: false,
                  imgWithCarousel: false,
                  title: false,
                  variantTitle: false,
                  options: showOptions,  // Control dropdown visibility with showOptions prop
                  price: !hidePrice,
                  description: false,
                  buttonWithQuantity: false,  // Never show button with quantity
                  button: showButton,  // Control button visibility with showButton prop
                  quantity: false,
                },
                styles: {
                  product: {
                    'text-align': isLeft ? 'left' : 'center',
                    '@media (min-width: 601px)': {
                      'max-width': '100%',
                      'margin-left': '0',
                      'margin-bottom': '0px',
                    },
                  },
                  variantTitle: {
                    'display': 'none !important',
                  },
                  variantLabel: {
                    'font-family': 'Lato, sans-serif',
                    'font-size': '13px',
                    'font-weight': '600',
                    'color': '#525252',
                    'text-transform': 'uppercase',
                    'letter-spacing': '0.5px',
                    'margin-bottom': '8px',
                    'display': 'block',
                  },
                  select: {
                    'font-family': 'Lato, sans-serif',
                    'font-size': '14px',
                    'font-weight': '500',
                    'color': '#313131',
                    'background-color': '#fff',
                    'border': '2px solid #e8e8e8',
                    'border-radius': '6px',
                    'padding': '10px 36px 10px 14px',
                    'cursor': 'pointer',
                    'transition': 'all 0.2s ease',
                    'appearance': 'none',
                    '-webkit-appearance': 'none',
                    '-moz-appearance': 'none',
                    'width': '100%',
                    'background-image': 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2300ad23\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                    'background-repeat': 'no-repeat',
                    'background-position': 'right 12px center',
                    'background-size': '18px',
                    'margin-bottom': '16px',
                    ':hover': {
                      'border-color': '#00ad23',
                      'background-color': '#f9f9f9',
                    },
                    ':focus': {
                      'outline': 'none',
                      'border-color': '#00ad23',
                      'box-shadow': '0 0 0 3px rgba(0, 173, 35, 0.1)',
                    },
                  },
                  price: {
                    'font-family': 'Lato, sans-serif',
                    'font-size': priceSize,
                    'font-weight': '800',
                    'color': '#00ad23',
                    'margin-bottom': compact ? '4px' : '20px',
                    'line-height': '1.2',
                  },
                  compareAt: {
                    'font-family': 'Lato, sans-serif',
                    'font-size': compareAtSize || (compact ? '13px' : '24px'),
                    'color': '#999999',
                    'font-weight': '400',
                    'text-decoration': 'line-through',
                    'margin-bottom': compact ? '4px' : '0',
                  },
                  button: outline
                    ? {
                        'font-family': 'Lato, sans-serif',
                        'font-size': '13px',
                        'font-weight': '600',
                        display: 'inline-block',
                        padding: '0 16px',
                        margin: '0',
                        'min-height': '40px',
                        height: '40px',
                        'box-sizing': 'border-box',
                        'background-color': 'transparent',
                        color: color || '#00ad23',
                        border: '2px solid ' + (color || '#00ad23'),
                        'border-radius': '4px',
                        ':hover': {
                          color: color || '#00ad23',
                          'background-color': 'rgba(0, 173, 35, 0.08)',
                          'border-color': color || '#00ad23',
                        },
                        ':focus': {
                          color: color || '#00ad23',
                          'background-color': 'rgba(0, 173, 35, 0.08)',
                          'border-color': color || '#00ad23',
                        },
                      }
                    : {
                        'font-family': 'Lato, sans-serif',
                        'font-size': compact ? '14px' : '13px',
                        'font-weight': '600',
                        'text-transform': compact ? 'none' : 'uppercase',
                        'letter-spacing': compact ? 'normal' : '0.8px',
                        display: 'inline-block',
                        padding: compact ? '6px 16px' : '0 16px',
                        margin: '0',
                        height: compact ? '36px' : '40px',
                        'min-height': compact ? '36px' : '40px',
                        'line-height': compact ? '1.75' : 'normal',
                        'box-sizing': 'border-box',
                        width: '100%',
                        'max-width': '100%',
                        'background-color': background || '#00ad23',
                        color: color || '#fff',
                        'border-radius': '4px',
                        border: 'none',
                        'box-shadow': compact ? 'none' : '0 2px 8px rgba(0, 173, 35, 0.2)',
                        transition: compact ? 'background-color 0.2s ease' : 'all 0.3s ease',
                        'white-space': 'nowrap',
                        ':hover': {
                          color: '#fff',
                          'background-color': '#009c1f',
                          'box-shadow': compact ? 'none' : '0 4px 12px rgba(0, 173, 35, 0.3)',
                          transform: compact ? 'none' : 'translateY(-1px)',
                        },
                        ':focus': {
                          color: '#fff',
                          'background-color': '#009c1f',
                          'box-shadow': compact ? 'none' : '0 0 0 3px rgba(0, 173, 35, 0.2)',
                        },
                        ':active': {
                          'background-color': '#008a1c',
                          transform: compact ? 'none' : 'translateY(0)',
                          'box-shadow': compact ? 'none' : '0 2px 6px rgba(0, 173, 35, 0.2)',
                        },
                        '@media (max-width: 767px)': {
                          width: '100%',
                          'max-width': '100%',
                          display: 'block',
                        },
                      },
                  buttonWrapper: {
                    'margin-top': '0',
                    'margin-bottom': '0',
                  },
                },
                googleFonts: ['Lato'],
              },
              cart: {
                contents: {
                  button: true,
                },
                styles: {
                  button: {
                    'font-family': 'Lato, sans-serif',
                    'font-size': '13px',
                    'padding-top': '14.5px',
                    'padding-bottom': '14.5px',
                    'font-weight': 'bold',
                    ':hover': {
                      'background-color': '#6ca44e',
                    },
                    ':focus': {
                      'background-color': '#00ad23',
                    },
                  },
                },
                googleFonts: ['Lato'],
              },
              modalProduct: {
                contents: {
                  img: false,
                  imgWithCarousel: true,
                  variantTitle: false,
                  buttonWithQuantity: true,
                  button: false,
                  quantity: false,
                },
                styles: {
                  product: {
                    '@media (min-width: 601px)': {
                      'max-width': '100%',
                      'margin-left': '0px',
                      'margin-bottom': '0px',
                    },
                  },
                },
                googleFonts: ['Lato'],
              },
              toggle: {
                styles: {
                  toggle: {
                    'font-family': 'Lato, sans-serif',
                    'font-weight': 'bold',
                    'background-color': '#00ad23',
                    ':hover': {
                      'background-color': '#6ca44e',
                    },
                    ':focus': {
                      'background-color': '#6ca44e',
                    },
                  },
                },
                googleFonts: ['Lato'],
              },
            },
          });

          // Store component reference for cleanup
          componentRef.current = componentResult;

          // createComponent returns a Promise that rejects if the product lookup
          // fails (e.g. a stale/unpublished buyButtonId) - without this, that
          // shows up only as an unhandled rejection and the widget stays blank.
          if (componentResult && typeof componentResult.then === 'function') {
            componentResult
              .then((component: any) => {
                componentRef.current = component;
              })
              .catch((error: unknown) => {
                console.warn(`AddToCart: Shopify product not found for buyButtonId ${buyButtonId}`, error);
                onError?.();
              });
          }
        });
      }

      loadShopifyBuySDK().then(ShopifyBuyInit);

      // Cleanup function to destroy component when unmounting
      return () => {
        if (componentRef.current) {
          try {
            componentRef.current.destroy();
          } catch (e) {
            // Component already destroyed
          }
        }
      };
    }
  }, [buyButtonId, id, background, color, hidePrice, outline, isLeft, priceSize, compareAtSize, showButton, showOptions, compact]);

  return <div id={`product-component-${id}`} />;
};

export default AddToCart;
